/* Shopify authenticates the identity; this UI never accepts customer IDs as authority. */
class ElixMembers extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.config = JSON.parse(
      this.querySelector("[data-member-config]").textContent,
    );
    this.s = this.config.strings;
    this.page = this.dataset.accountPage || "overview";
    const pageTitle = {profile:this.s.title,orders:this.s.orders,tools:"ELIX Tools"}[this.page];
    if (pageTitle) document.title = pageTitle + " – ELIX LABS";
    this.status = this.querySelector("[data-member-status]");
    const login = this.querySelector("[data-member-login]");
    const loginUrl = new URL(this.config.login || '/customer_authentication/login', location.origin);
    loginUrl.searchParams.set('return_to', location.pathname + location.search);
    loginUrl.searchParams.set('locale', this.config.locale);
    login.href = loginUrl.href;
    this.load();
  }
  async api(path, data) {
    const r = await fetch(this.config.proxy + path, {
      method: data === undefined ? "GET" : "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      ...(data === undefined
        ? {}
        : { body: JSON.stringify({ ...data, csrf: this.csrf }) }),
    });
    let d;
    try {
      d = await r.json();
    } catch {
      throw Error("service_unavailable");
    }
    if (!r.ok) {
      const error = Error(d.error || "service_unavailable");
      error.status = r.status;
      throw error;
    }
    return d;
  }
  node(tag, text) {
    const n = document.createElement(tag);
    if (text !== undefined) n.textContent = text;
    return n;
  }
  message(error) {
    if (error.status === 401) {
      this.querySelector("[data-member-content]").hidden = true;
      this.querySelector("[data-member-guest]").hidden = false;
    }
    return this.s[error.message] || this.s.error;
  }
  async load() {
    if (!this.config.enabled) {
      this.status.textContent = this.s.unavailable;
      return;
    }
    try {
      const me = await this.api("/me");
      this.csrf = me.csrf;
      if (!me.authenticated) {
        this.status.textContent = "";
        this.querySelector("[data-member-guest]").hidden = false;
        return;
      }
      this.me = me;
      this.querySelector("[data-member-content]").hidden = false;
      this.querySelector("[data-member-welcome]").textContent =
        this.s.welcome.replace("{name}", me.profile.firstName || "");
      this.status.textContent = "";
      this.renderProfile(me.profile);
      this.renderCode(me);
      const rules = this.node(
        "p",
        this.s.rules +
          ": " +
          me.rules.percentage +
          "% · " +
          this.s.eligible_minimum +
          ": " +
          this.amount(me.rules.minimumSubtotal),
      );
      this.querySelector("#MemberRewards").append(rules);
      this.renderReferrals(me.referrals);
      this.localOrders = me.orders;
      if (this.page === 'overview' || this.page === 'orders') await this.orders();
      if (this.page === 'tools') {
        const resources=await this.api('/resources');
        const target=this.querySelector('[data-member-video]');
        if(resources.video && target){const url=new URL(resources.video.url);if(url.protocol==='https:'){const link=this.node('a',resources.video.title[this.config.locale]||resources.video.title.en||this.s.video);link.href=url.href;link.rel='noopener';link.target='_blank';target.append(link);target.hidden=false;}}
      }
    } catch (e) {
      this.status.textContent = this.message(e);
    }
  }
  renderProfile(p) {
    const form = this.querySelector('[data-profile-form]');
    const fields = form.querySelector('[data-profile-fields]');
    const values = {firstName:p.firstName||'',lastName:p.lastName||'',email:p.defaultEmailAddress?.emailAddress||'',address1:p.defaultAddress?.address1||'',phone:p.defaultPhoneNumber?.phoneNumber||''};
    for (const [key,value] of Object.entries(values)) form.elements.namedItem(key).value=value;
    fields.disabled=false;
    form.onsubmit=async(event)=>{
      event.preventDefault();
      if(fields.disabled || !form.reportValidity())return;
      const data=Object.fromEntries(['firstName','lastName','email','address1','phone'].map(key=>[key,form.elements.namedItem(key).value.trim()]));
      const status=form.querySelector('[data-profile-status]');
      fields.disabled=true;status.textContent=this.s.loading;
      try{
        await this.api('/profile',data);
        this.querySelector('[data-member-welcome]').textContent=this.s.welcome.replace('{name}',data.firstName);
        status.textContent=this.s.profile_saved;
      }catch(e){status.textContent=this.message(e);}
      finally{fields.disabled=false;}
    };
    this.querySelector("[name=subscribed]").checked =
      p.emailMarketingConsent?.marketingState === "SUBSCRIBED";
    this.querySelector("[data-marketing-form]").onsubmit = async (event) => {
      event.preventDefault();
      const button = event.currentTarget.querySelector("button");
      button.disabled = true;
      try {
        await this.api("/marketing", {
          subscribed: this.querySelector("[name=subscribed]").checked,
        });
        this.querySelector("[data-marketing-status]").textContent =
          this.s.saved;
      } catch (e) {
        this.querySelector("[data-marketing-status]").textContent =
          this.message(e);
      } finally {
        button.disabled = false;
      }
    };
  }
  renderCode(me) {
    const code = me.code;
    if (!code) {
      this.querySelector("[data-code-state]").textContent = this.s.unavailable;
      return;
    }
    this.querySelector("[data-member-code]").textContent = code.code;
    this.querySelector("[data-code-state]").textContent = !code.active
      ? this.s.suspended
      : code.expiresAt && code.expiresAt < Date.now()
        ? this.s.code_expired
        : code.expiresAt
          ? this.s.expires +
            ": " +
            new Intl.DateTimeFormat(this.config.locale).format(code.expiresAt)
          : "";
    for (const button of this.querySelectorAll(
      "[data-copy-code],[data-copy-link],[data-share]",
    ))
      button.disabled =
        !code.active || (!!code.expiresAt && code.expiresAt < Date.now());
    const url = new URL(this.config.cart, location.origin);
    url.searchParams.set("ref", code.code);
    this.querySelector("[data-copy-code]").onclick = () => this.copy(code.code);
    this.querySelector("[data-copy-link]").onclick = () => this.copy(url.href);
    const share = this.querySelector("[data-share]");
    if (navigator.share) {
      share.hidden = false;
      share.onclick = async () => {
        try {
          await navigator.share({
            title: "ELIX LABS",
            text: code.code,
            url: url.href,
          });
        } catch {
          /* User cancelled the native share sheet. */
        }
      };
    }
  }
  async copy(text) {
    try {
      await navigator.clipboard.writeText(text);
      this.querySelector("[data-copy-status]").textContent = this.s.copied;
    } catch {
      this.querySelector("[data-copy-status]").textContent = this.s.copy_error;
    }
  }
  renderReferrals(rows) {
    const target = this.querySelector("[data-member-referrals]");
    target.replaceChildren();
    if (!rows.length) {
      target.textContent = this.s.no_referrals;
      return;
    }
    const list = this.node("ul");
    for (const row of rows)
      list.append(
        this.node(
          "li",
          (this.s[row.state] || row.state) +
            " · " +
            new Intl.DateTimeFormat(this.config.locale).format(row.updated_at),
        ),
      );
    target.append(list);
  }
  amount(cents) {
    return new Intl.NumberFormat(this.config.locale, {
      style: "currency",
      currency: "EUR",
    }).format(cents / 100);
  }
  renderOrderDetails(details, order) {
    const dl = this.node("dl");
    for (const [k, v] of [
      [
        this.s.payment,
        this.s["status_" + order.displayFinancialStatus.toLowerCase()] ||
          order.displayFinancialStatus,
      ],
      [
        this.s.fulfillment,
        this.s["status_" + order.displayFulfillmentStatus.toLowerCase()] ||
          order.displayFulfillmentStatus,
      ],
    ]) {
      const row = this.node("div");
      row.append(this.node("dt", k), this.node("dd", v));
      dl.append(row);
    }
    for (const [label, bag] of [
      [this.s.discount, order.totalDiscountsSet],
      [this.s.tax, order.totalTaxSet],
    ]) {
      if (bag) {
        const row = this.node("div");
        row.append(
          this.node("dt", label),
          this.node(
            "dd",
            bag.shopMoney.amount + " " + bag.shopMoney.currencyCode,
          ),
        );
        dl.append(row);
      }
    }
    for (const shipping of order.shippingLines.nodes) {
      const row = this.node("div");
      row.append(
        this.node("dt", this.s.shipping),
        this.node(
          "dd",
          shipping.discountedPriceSet.shopMoney.amount +
            " " +
            shipping.discountedPriceSet.shopMoney.currencyCode,
        ),
      );
      dl.append(row);
    }
    details.append(dl);
    for (const line of order.lineItems.nodes)
      details.append(
        this.node(
          "p",
          line.quantity +
            " × " +
            (line.product?.handle === "research-kit"
              ? this.s.kit_name
              : line.product?.handle === "bacteriostatic-water"
                ? this.s.water_name
                : line.title) +
            " · " +
            line.discountedTotalSet.shopMoney.amount +
            " " +
            line.discountedTotalSet.shopMoney.currencyCode,
        ),
      );
    if (order.fulfillments.length) {
      for (const f of order.fulfillments)
        for (const tracking of f.trackingInfo) {
          const p = this.node(
            "p",
            this.s.tracking +
              ": " +
              (tracking.company || "") +
              " " +
              (tracking.number || ""),
          );
          if (tracking.url && new URL(tracking.url).protocol === "https:") {
            const link = this.node("a", this.s.tracking);
            link.href = tracking.url;
            p.append(" ", link);
          }
          details.append(p);
        }
    } else details.append(this.node("p", this.s.no_tracking));
    if (
      order.statusPageUrl &&
      new URL(order.statusPageUrl).protocol === "https:"
    ) {
      const link = this.node("a", this.s.details);
      link.href = order.statusPageUrl;
      details.append(link);
    }
  }
  async orders(cursor) {
    const target = this.querySelector("[data-member-orders]"),
      data = await this.api(
        "/orders" + (cursor ? "?after=" + encodeURIComponent(cursor) : ""),
      );
    if (!cursor) {
      target.replaceChildren();
      if (this.page === 'orders') {
        this.orderGroups={};
        for(const key of ['current','past']){const group=this.node('section');group.className='emp-order-group';group.append(this.node('h3',this.s['orders_'+key]));const list=this.node('div');list.dataset.orderGroup=key;group.append(list);target.append(group);this.orderGroups[key]=list;}
      }
    }
    if(this.orderGroups) for(const list of Object.values(this.orderGroups))list.querySelector('[data-order-empty]')?.remove();
    if (data.historyLimited && !cursor)
      target.append(this.node("p", this.s.history_limited));
    const seen = new Set();
    for (const order of data.nodes) {
      seen.add(order.id);
      const a = this.node("article");
      a.className = "em-order";
      const details = this.node("details");
      details.className = "em-details";
      details.append(
        this.node(
          "summary",
          order.name +
            " · " +
            order.currentTotalPriceSet.shopMoney.amount +
            " " +
            order.currentTotalPriceSet.shopMoney.currencyCode,
        ),
      );
      const body = this.node("div");
      details.append(body);
      if (order.lineItems) this.renderOrderDetails(body, order);
      else {
        let loaded = false,
          loading = false;
        details.addEventListener("toggle", async () => {
          if (!details.open || loaded || loading) return;
          loading = true;
          body.textContent = this.s.loading;
          try {
            const full = await this.api(
              "/shopify-orders/" + order.id.split("/").at(-1),
            );
            body.replaceChildren();
            this.renderOrderDetails(body, full);
            loaded = true;
          } catch (e) {
            body.textContent = this.message(e);
          } finally {
            loading = false;
          }
        });
      }
      a.append(details);
      const finished = !!order.cancelledAt || ['REFUNDED','VOIDED'].includes(order.displayFinancialStatus) || order.displayFulfillmentStatus==='FULFILLED' || (order.displayFinancialStatus==='PAID' && order.displayFulfillmentStatus==='FULFILLMENT_NOT_REQUIRED');
      (this.orderGroups ? this.orderGroups[finished?'past':'current'] : target).append(a);
    }
    if (!cursor)
      for (const order of this.localOrders || []) {
        if (!seen.has(order.remoteOrderId)) {
          const p = this.node(
            "p",
            order.reference +
              " · " +
              this.s.recording +
              " · " +
              this.amount(order.total),
          );
          (this.orderGroups?.current || target).prepend(p);
        }
      }
    if(this.orderGroups){for(const list of Object.values(this.orderGroups))if(!list.childNodes.length){const empty=this.node('p',this.s.empty_orders);empty.dataset.orderEmpty='';list.append(empty);}}
    else if (!target.childNodes.length) target.textContent = this.s.empty_orders;
    const more = this.querySelector("[data-more-orders]");
    more.hidden = !data.pageInfo.hasNextPage;
    more.onclick = () =>
      this.orders(data.pageInfo.endCursor).catch((e) => {
        this.status.textContent = this.message(e);
      });
  }
}
if (!customElements.get("elix-members"))
  customElements.define("elix-members", ElixMembers);
